import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiHeadersService {
  private readonly secureEndpoints: string[] = [
    '/auth',
    '/payment'
  ];

  /**
   * Generates headers for API requests with optional secure headers for sensitive endpoints
   * @param url The API endpoint URL
   * @param params Optional additional headers
   * @param isSecure Whether to include secure headers (defaults to false)
   * @returns Object containing headers and optional params
   */
  generateHeaders(url: string, params: any = {}, isSecure: boolean = false) {
    // Get access token
    const accessToken = localStorage.getItem("userToken");
    
    // Basic headers
    let headers: any = {
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
      Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      'x_api_key': environment.apiKey
    };

    // Add any additional params to headers
    if (params) {
      Object.keys(params).forEach(key => {
        headers[key] = params[key];
      });
    }


    // Check if this is a secure endpoint by looking for auth or payment in the URL
    const isSecureEndpoint = url.includes('/auth') || url.includes('/payment');


    // Add secure headers if needed
    if (isSecure || isSecureEndpoint) {
      const secureHeaders = this.generateSecureHeaders();
      headers = { ...headers, ...secureHeaders };
    }

    // Prepare request data
    const reqData = {
      headers: headers
    };

    // If there are params, add them to the request data
    if (params) {
      let reqParams = {};
      Object.keys(params).forEach(key => {
        reqParams[key] = params[key];
      });
      reqData['params'] = reqParams;
    }

    return reqData;
  }

  /**
   * Generates secure headers for sensitive endpoints
   * @returns Object containing security headers
   */
  private generateSecureHeaders(): { [header: string]: string } {
    // Generate random public token (15-16 chars)
    const publicToken = this.generateRandomString(15, 16);

    // Generate random nonce (6-7 chars)
    const nonce = this.generateRandomString(6, 7);

    // Current timestamp
    const timestamp = Date.now().toString();

    // Get secret key from environment
    const secretKey = environment.secKey;

    // Create data string to sign
    const dataToSign = `${publicToken}:${timestamp}:${secretKey}:${nonce}`;

    if (!secretKey) {
      // console.error('Secret key is not configured');
      return {}; // Return empty headers if no secret key
    }

    // Generate HMAC-SHA256 signature
    const signature = CryptoJS.HmacSHA256(dataToSign, secretKey).toString(CryptoJS.enc.Hex);

    return {
      'x-public-token': publicToken,
      'x-timestamp': timestamp,
      'x-nonce': nonce,
      'x-signature': signature
    };
  }

  /**
   * Generates a random string of specified length
   * @param minLength Minimum length of the string
   * @param maxLength Maximum length of the string
   * @returns Random string
   */
  private generateRandomString(minLength: number, maxLength: number): string {
    const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
} 